/**
 * The Systems and Overview boards' last memory-only entries, carried to the
 * platform (build 7).
 *
 *   carousel.fault   a carousel breakdown, recorded as finished corrective
 *                    work on the day it happened (POST /v1/work-orders/
 *                    record-completed): which machine, what failed, how long
 *                    it was down, whether the belt was renewed. The board's
 *                    failure model reads it back, so the prediction no longer
 *                    forgets a breakdown on reload.
 *   alarm.ack        an acknowledged alarm on the Overview, as a site note
 *                    every other screen reads, like anomaly.ack (build 2).
 *
 * Neither raises or moves a work order of the boards' own: those paths are
 * another build's.
 *
 * Nothing here imports the demo domain: this sits in the entry chunk.
 */
import type { CompletedWorkInput } from "../../../domain/friction";
import { t } from "../../../i18n";
import { registerWrite } from "../writes";

/** The note topic an alarm acknowledgement is kept under. */
export const ALARM_ACK_TOPIC = "overview.alarm-ack";
/** The work an activity a breakdown is recorded as. */
export const CAROUSEL_BREAKDOWN = "carousel_breakdown";

/** A breakdown as the board keeps it (`CAR_FAULTS[]`). */
export interface CarouselFault {
  id: string;
  car: string;
  mode: string;
  /** YYYY-MM-DD */
  at: string;
  /** minutes the machine was down */
  down?: number;
  note?: string;
}

export interface CarouselExtra {
  /** the machine's name on the board */
  name?: string;
  /** what failed, in words (`CAR_MODES[].label`) */
  modeLabel?: string;
  beltReplaced?: boolean;
}

/** A breakdown as finished corrective work. */
export function breakdownBody(f: CarouselFault, extra: CarouselExtra, siteId: string, key: string): CompletedWorkInput {
  const down = Math.max(0, Math.round(Number(f.down) || 0));
  const what = extra.modeLabel || f.mode;
  return {
    siteId,
    title: `${f.car}${extra.name ? ` ${extra.name}` : ""} — ${what}`.slice(0, 200),
    activity: CAROUSEL_BREAKDOWN,
    completedOn: f.at,
    clientId: key,
    kind: "corrective",
    /* a long stoppage is the one to look at first */
    priority: down > 120 ? 2 : 3,
    description: [f.note?.trim(), `${down} minutes down.`, extra.beltReplaced ? "Belt replaced." : ""]
      .filter(Boolean)
      .join(" ")
      .slice(0, 4000),
    attrs: {
      equipment: f.car,
      failure_mode: f.mode,
      downtime_minutes: down,
      belt_replaced: !!extra.beltReplaced,
    },
  };
}

registerWrite("carousel.fault", {
  permission: () => "work_order:write",
  invalidate: (siteId) => [["work-orders", siteId]],
  /* idempotent on the breakdown's key: trying again cannot record it twice */
  retryable: true,
  async send(ctx, rec: CarouselFault, extra: CarouselExtra | null) {
    const r = await ctx.source.recordCompletedWork(breakdownBody(rec, extra ?? {}, ctx.siteId, ctx.key));
    return {
      id: r.id,
      reference: r.number,
      at: r.completedAt,
      kind: "work_order",
      text: t("Breakdown recorded on the platform as {ref}.", { ref: r.number }),
    };
  },
});

/** An alarm as the Overview keeps it (`ALARMS[key]`). */
export interface BoardAlarm {
  key: string;
  label?: string;
  /** when this browser saw it begin */
  since?: string | Date;
}

registerWrite("alarm.ack", {
  permission: () => "note:write",
  invalidate: (site) => [["notes", site, ALARM_ACK_TOPIC]],
  /* a second note saying the same thing changes nothing anyone reads */
  retryable: true,
  async send(ctx, rec: BoardAlarm) {
    if (!rec?.key) throw new Error(t("Nothing to acknowledge."));
    const since = rec.since instanceof Date ? rec.since.toISOString() : rec.since;
    await ctx.source.addNote(ctx.siteId, {
      topic: ALARM_ACK_TOPIC,
      category: "observation",
      body: JSON.stringify({ key: rec.key, label: rec.label ?? "", since: since ?? null, act: "ack" }),
    });
    return { id: "", reference: "", text: t("Alarm acknowledgement saved on the platform.") };
  },
});
